import React, { useState, useEffect } from "react";
import { useSubscription } from "../../context/SubscriptionContext";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../../config/apiConfig"; // Make sure this path is correct

// Badge component
const Badge = ({ price, per }) => (
  <div className="relative w-20 h-20">
    <img
      src="https://cdn.acsdev.in/Rentastic/ultra.png"
      alt="badge"
      className="w-full h-full object-contain -mt-4"
    />
    <div className="absolute inset-0 flex items-center justify-center -mt-4 text-center">
      <span className="text-white text-sm font-semibold leading-tight">
        {price}
        <br />
        <span className="text-xs font-light">{per}</span>
      </span>
    </div>
  </div>
);

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plans, loading, addToCart, token, refreshUserSubscription } = useSubscription();

  const [activePlanId, setActivePlanId] = useState(null);

  // Load active plan_id from localStorage on mount
  useEffect(() => {
    const planId = localStorage.getItem("plan_id");
    if (planId) setActivePlanId(planId);
  }, []);

  // Update active plan if subscription just purchased via localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const planId = localStorage.getItem("plan_id");
      setActivePlanId(planId);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch latest active subscription from API
  useEffect(() => {
    if (!token) return;
    const userId = localStorage.getItem("user_id");
    if (!userId) return;

    const fetchActiveSubscription = async () => {
      try {
        const res = await fetch(`${BASE_URL}/mysubscriptions/${userId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (data.status === 200 && Array.isArray(data.data)) {
          const activeSub = data.data.find(sub =>(sub.status === "active" && sub.payment_status === "paid"));

          if (activeSub) {
            localStorage.setItem("sub_id", activeSub.id);
            setActivePlanId(activeSub.plan_id);
          } else {
            setActivePlanId(null);
            localStorage.removeItem("sub_id");
            localStorage.removeItem("plan_id");
          }
        }
      } catch (err) {
        console.error("Failed to fetch active subscription:", err);
      }
    };

    fetchActiveSubscription();

    // Optional: refresh every 30 seconds to auto-update status without reload
    const interval = setInterval(fetchActiveSubscription, 30000);
    return () => clearInterval(interval);

  }, [token]);

  if (loading)
    return <div className="text-center py-10 text-gray-600">Loading plans...</div>;

  const handleChoosePlan = async (plan) => {
    if (activePlanId) {
      alert("You already have an active subscription!");
      return;
    }

    // Add plan to cart first
    addToCart(plan);

    // Navigate to checkout/payment page
    navigate("/cartSection", { state: { fromSubscription: true } });
  };

  const handleSubscriptionSuccess = (planId, subId) => {
    setActivePlanId(planId);
    localStorage.setItem("plan_id", planId);
    localStorage.setItem("sub_id", subId);

    if (refreshUserSubscription) refreshUserSubscription(subId);
  };

  const anyPlanActive = !!activePlanId;

  return (
    <div className="max-w-6xl w-full grid grid-cols-1 sm:grid-cols-2 gap-6">
      {plans.map((plan) => {
        const isActive = String(plan.id) === String(activePlanId);

        return (
          <div
            key={plan.id}
            className={`bg-white rounded-2xl shadow-lg p-6 flex flex-col justify-between relative transition transform ${
              isActive ? "ring-4 ring-green-600 scale-105" : ""
            }`}
          >
            <div className="absolute top-2 right-2">
              <Badge price={plan.price} per={plan.per} />
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              {plan.name}
            </h3>

            <ul className="space-y-2 mb-4 text-gray-600 text-sm">
              {plan.features?.map((feature, idx) => (
                <li key={idx}>✔ {feature}</li>
              ))}
            </ul>

            <button
              onClick={() => handleChoosePlan(plan)}
              className={`py-2 rounded-2xl w-full border-2 transition ${
                isActive
                  ? "bg-green-800 text-white border-green-800 cursor-not-allowed"
                  : anyPlanActive
                  ? "bg-gray-300 text-gray-600 border-gray-300 cursor-not-allowed"
                  : "bg-white text-green-800 border-green-800 hover:bg-green-800 hover:text-white"
              }`}
              disabled={anyPlanActive} // disable all buttons if any plan is active
            >
              {isActive ? "Active Plan" : anyPlanActive ? "Upgrade" : "Choose Plan"}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default Subscription;
